import { memo, useCallback, useRef } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import { SkeletonLine } from 'components/Skeleton'
import SeverityQuickEdit from 'components/SeverityQuickEdit'
import DueCell from './DueCell'
import LoadError from './LoadError'
import OwnerCell from './OwnerCell'
import RowMenu from './RowMenu'
import TaskProgress from './TaskProgress'
import { hideEmail } from 'utils/user'
import { formatDay, relativeTime } from './remedialDates'
import { canRejectAiFind, sourceLabel } from './remedialMeta'
import { sortState } from './remedialsQuery'
import { ACCENT_SOFT, BORDER, INK, LINK, MUTED, PrimaryButton, SecondaryButton } from './ui'

const COLUMNS = [
  { key: 'severity', label: 'Severity', width: '104px' },
  { key: 'title', label: 'Remedial', width: null },
  { key: 'due', label: 'Due', width: '164px' },
  { key: 'owner', label: 'Owner', width: '156px' },
  { key: 'stage', label: 'Task', width: '156px' },
  { key: 'created', label: 'Created', width: '108px' },
]

const SKELETON_ROWS = 8

export const isSelectable = action => !!(action.permissions?.canUpdate || canRejectAiFind(action) || action.permissions?.canConvert)

const stop = e => e.stopPropagation()

const SourceText = ({ action, onOpenSource }) => {
  if (action.sourceFile?.displayName) {
    if (action.sourceFile.deleted) return <span>{action.sourceFile.displayName} (deleted)</span>
    return (
      <SourceLink as='button' type='button' title={`Open ${action.sourceFile.displayName}`} onClick={e => { e.stopPropagation(); onOpenSource(action) }}>
        {action.sourceFile.displayName}
      </SourceLink>
    )
  }
  if (action.source === 'incident' && action.sourceIncidentId) {
    return <SourceLink to={`/incidents/${action.sourceIncidentId}`} onClick={stop}>{sourceLabel(action.source)}</SourceLink>
  }
  if (action.source === 'task_form' && action.sourceTask?.id) {
    return <SourceLink to={`/tasks/${action.sourceTask.id}`} onClick={stop}>{sourceLabel(action.source)}</SourceLink>
  }
  return <span>{sourceLabel(action.source)}</span>
}

export const commentCountLabel = count => `${count} comment${count === 1 ? '' : 's'}`

export const CommentLine = ({ action, onOpenComments }) => {
  const latest = action.latestComment
  const count = action.commentsCount || 0
  if (!latest && count === 0) return null
  const author = typeof latest?.author === 'string' && latest.author.trim() ? hideEmail(latest.author.trim()) : null
  const when = latest ? relativeTime(latest.createdAt) : ''
  const byline = [author, when].filter(Boolean).join(', ')
  return (
    <CommentRow data-testid='remedial-latest-comment'>
      {latest && (
        <CommentText title={latest.excerpt}>
          <ChatBubbleOutlineIcon aria-hidden='true' />
          <CommentExcerpt>“{latest.excerpt}”</CommentExcerpt>
          {byline && <CommentBy>· {byline}</CommentBy>}
        </CommentText>
      )}
      {count > 0 && (
        <CommentCount
          type='button'
          onClick={e => { e.stopPropagation(); onOpenComments?.(action) }}
          aria-label={`${commentCountLabel(count)}. Open the remedial and read its comments`}
        >
          {commentCountLabel(count)}
        </CommentCount>
      )}
    </CommentRow>
  )
}

const SortIcon = ({ active, dir }) => {
  if (!active) return <UnfoldMoreIcon aria-hidden='true' className='sort-idle' />
  return dir === 'asc' ? <ArrowUpwardIcon aria-hidden='true' /> : <ArrowDownwardIcon aria-hidden='true' />
}

const RemedialRow = memo(({
  action, checked, showBuilding, onToggle, onOpen, onCreateTask, onComplete, onDismiss,
  onChangeOwner, onChangeDue, onChangeSeverity, onOpenSource, onOpenComments,
}) => {
  const perms = action.permissions || {}
  const building = showBuilding ? action.buildingPassport?.name : null
  return (
    <Tr data-remedial-id={action.id} $selected={checked} $overdue={action.isOverdue === true} onClick={() => onOpen(action)}>
      <Td data-label='Select' $check onClick={stop}>
        {isSelectable(action) && (
          <Check>
            <input type='checkbox' checked={checked} onChange={() => onToggle(action.id)} aria-label={`Select ${action.title}`} />
          </Check>
        )}
      </Td>
      <Td data-label='Severity' onClick={stop}>
        <SeverityQuickEdit value={action.severity} size='sm' disabled={!perms.canUpdate} onChange={next => onChangeSeverity(action, next)} />
      </Td>
      <Td data-label='Remedial' $main>
        <TitleButton type='button' title={action.title} onClick={e => { e.stopPropagation(); onOpen(action) }}>{action.title}</TitleButton>
        <Sub>
          {building && <SubPart title={building}>{building}</SubPart>}
          {building && <span aria-hidden='true'>·</span>}
          <SubPart><SourceText action={action} onOpenSource={onOpenSource} /></SubPart>
        </Sub>
        <CommentLine action={action} onOpenComments={onOpenComments} />
      </Td>
      <Td data-label='Due' onClick={stop}><DueCell action={action} onChange={onChangeDue} variant='row' /></Td>
      <Td data-label='Owner' onClick={stop}><OwnerCell action={action} onChange={onChangeOwner} variant='row' /></Td>
      <Td data-label='Task'><TaskProgress action={action} /></Td>
      <Td data-label='Created' data-col='created'><Created>{formatDay(action.createdAt)}</Created></Td>
      <Td data-label='Actions' $end onClick={stop}>
        <RowMenu action={action} onOpen={onOpen} onCreateTask={onCreateTask} onComplete={onComplete} onDismiss={onDismiss} />
      </Td>
    </Tr>
  )
})

const RemedialsTable = ({
  actions, meta, loading, error = false, onRetry, sort, selectedIds, emptyText, showBuilding,
  onSort, onSelect, onLoadMore, onOpen, onCreateTask, onComplete, onDismiss,
  onChangeOwner, onChangeDue, onChangeSeverity, onOpenSource, onOpenComments, onAdd, onClearFilters,
}) => {
  const { column, dir } = sortState(sort)
  const selectable = actions.filter(isSelectable)
  const allSelected = selectable.length > 0 && selectable.every(action => selectedIds.includes(action.id))
  const selectedRef = useRef(selectedIds)
  selectedRef.current = selectedIds
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect
  const toggle = useCallback(id => {
    const current = selectedRef.current
    onSelectRef.current(current.includes(id) ? current.filter(x => x !== id) : [...current, id])
  }, [])

  if (error && actions.length === 0) {
    return <Panel><LoadError onRetry={onRetry} /></Panel>
  }
  if (loading && actions.length === 0) {
    return (
      <Panel $flush aria-busy='true' aria-label='Loading remedials'>
        {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
          <SkeletonRow key={i}>
            <SkeletonLine $w='18px' $h={18} />
            <SkeletonLine $w='72px' $h={20} />
            <SkeletonStack>
              <SkeletonLine $w='60%' $h={14} />
              <SkeletonLine $w='35%' $h={12} />
            </SkeletonStack>
            <SkeletonLine $w='110px' $h={14} />
            <SkeletonLine $w='120px' $h={14} />
            <SkeletonLine $w='96px' $h={20} />
          </SkeletonRow>
        ))}
      </Panel>
    )
  }
  if (actions.length === 0) {
    return (
      <Panel>
        <Empty>{emptyText}</Empty>
        {(onAdd || onClearFilters) && (
          <EmptyActions>
            {onAdd && <PrimaryButton type='button' onClick={onAdd}>Add remedial</PrimaryButton>}
            {onClearFilters && <SecondaryButton type='button' onClick={onClearFilters}>Clear filters</SecondaryButton>}
          </EmptyActions>
        )}
      </Panel>
    )
  }

  return (
    <>
      <Scroll>
        <Table>
          <colgroup>
            <col style={{ width: '48px' }} />
            {COLUMNS.map(col => <col key={col.key} data-col={col.key} style={col.width ? { width: col.width } : undefined} />)}
            <col style={{ width: '52px' }} />
          </colgroup>
          <thead>
            <tr>
              <Th $check>
                <Check>
                  <input
                    type='checkbox'
                    checked={allSelected}
                    onChange={() => onSelect(allSelected ? [] : selectable.map(action => action.id))}
                    aria-label={allSelected ? 'Clear selection' : `Select all ${selectable.length} loaded remedials`}
                  />
                </Check>
              </Th>
              {COLUMNS.map(col => {
                const active = column === col.key
                return (
                  <Th key={col.key} data-col={col.key} aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                    <SortButton type='button' $active={active} onClick={() => onSort(col.key)} title={`Sort by ${col.label.toLowerCase()}`}>
                      {col.label}
                      <SortIcon active={active} dir={dir} />
                    </SortButton>
                  </Th>
                )
              })}
              <Th><VisuallyHidden>Actions</VisuallyHidden></Th>
            </tr>
          </thead>
          <tbody>
            {actions.map(action => (
              <RemedialRow
                key={action.id}
                action={action}
                checked={selectedIds.includes(action.id)}
                showBuilding={showBuilding}
                onToggle={toggle}
                onOpen={onOpen}
                onCreateTask={onCreateTask}
                onComplete={onComplete}
                onDismiss={onDismiss}
                onChangeOwner={onChangeOwner}
                onChangeDue={onChangeDue}
                onChangeSeverity={onChangeSeverity}
                onOpenSource={onOpenSource}
                onOpenComments={onOpenComments}
              />
            ))}
          </tbody>
        </Table>
      </Scroll>
      {error && <ErrorRow><LoadError onRetry={onRetry} /></ErrorRow>}
      <InfiniteScrollSentinel hasMore={!error && !!meta && meta.currentPage < meta.lastPage} loading={loading} onLoadMore={onLoadMore} label='Loading more remedials…' />
    </>
  )
}

export default RemedialsTable

const Panel = styled.div`
  padding: ${({ $flush }) => ($flush ? '4px 0' : '16px')};
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 12px;
`

const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: 18px 72px minmax(0, 1fr) 110px 120px 96px;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  border-top: 1px solid #f4f4f5;
  &:first-child { border-top: none; }
  @media (max-width: 768px) {
    grid-template-columns: 72px minmax(0, 1fr);
    & > :nth-child(1), & > :nth-child(4), & > :nth-child(5), & > :nth-child(6) { display: none; }
  }
`

const SkeletonStack = styled.span`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Empty = styled.p`
  margin: 8px 0;
  font-size: 15px;
  color: ${MUTED};
`

const EmptyActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
`

const Scroll = styled.div`
  overflow-x: auto;
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 12px;
  @media (max-width: 768px) {
    overflow-x: visible;
    background: none;
    border: none;
  }
`

const Table = styled.table`
  width: 100%;
  min-width: 760px;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 14px;
  color: ${INK};
  @media (max-width: 1439px) {
    [data-col='created'] { display: none; }
  }
  @media (max-width: 768px) {
    display: block;
    min-width: 0;
    colgroup, thead { display: none; }
    tbody { display: block; width: 100%; }
    tr { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; width: 100%; box-sizing: border-box; }
    td { display: flex; box-sizing: border-box; }
  }
`

const Th = styled.th`
  position: sticky;
  top: 0;
  z-index: 1;
  box-sizing: border-box;
  padding: ${({ $check }) => ($check ? '0 0 0 6px' : '0 6px')};
  height: 44px;
  border-bottom: 2px solid ${BORDER};
  text-align: left;
  white-space: nowrap;
  background: #fff;
`

const SortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-height: 40px;
  padding: 0 4px;
  border: none;
  background: none;
  font: inherit;
  font-size: 12.5px;
  font-weight: ${({ $active }) => ($active ? 700 : 600)};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ $active }) => ($active ? INK : MUTED)};
  cursor: pointer;
  svg { font-size: 15px; margin-left: 2px; }
  .sort-idle { opacity: 0.45; }
  &:hover { color: ${INK}; .sort-idle { opacity: 1; } }
  &:focus-visible { outline: 2px solid ${INK}; outline-offset: 2px; }
`

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`

const Tr = styled.tr`
  cursor: pointer;
  background: ${({ $selected }) => ($selected ? ACCENT_SOFT : '#fff')};
  transition: background-color 0.12s ease;
  &:hover { background: ${({ $selected }) => ($selected ? ACCENT_SOFT : '#fafafa')}; }
  & > td { border-bottom: 1px solid #f1f1f3; }
  &:last-child > td { border-bottom: none; }
  @media (max-width: 768px) {
    position: relative;
    margin-bottom: 8px;
    padding: 4px 4px 8px 4px;
    row-gap: 6px;
    background: #fff;
    border: 1px solid ${BORDER};
    border-left: 3px solid ${({ $overdue }) => ($overdue ? '#b00020' : BORDER)};
    border-radius: 12px;
    & > td { border-bottom: none; }
  }
`

const Td = styled.td`
  box-sizing: border-box;
  padding: ${({ $check }) => ($check ? '4px 0 4px 6px' : '4px 6px')};
  height: 56px;
  vertical-align: middle;
  overflow: hidden;
  text-align: ${({ $end }) => ($end ? 'right' : 'left')};
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    flex: 0 1 auto;
    height: auto;
    min-width: 0;
    padding: 0;
    overflow: visible;
    &[data-label='Select'] { order: 1; flex: 0 0 40px; align-self: flex-start; }
    &[data-label='Remedial'] {
      order: 2;
      flex: 1 1 calc(100% - 96px);
      flex-direction: column;
      align-items: stretch;
      gap: 0;
      padding-top: 6px;
    }
    &[data-label='Actions'] { order: 3; flex: 0 0 40px; align-self: flex-start; }
    &[data-label='Severity'] { order: 4; }
    &[data-label='Due'] { order: 5; }
    &[data-label='Task'] { order: 6; min-height: 32px; }
    &[data-label='Owner'] { order: 7; }
    &[data-label='Created'] { display: none; }
    &[data-label='Severity'] button, &[data-label='Due'] button, &[data-label='Owner'] button {
      position: relative;
      min-height: 32px;
      padding-top: 0;
      padding-bottom: 0;
    }
    &[data-label='Due'] > span, &[data-label='Owner'] > span { min-height: 32px; padding: 0 4px; }
    &[data-label='Severity'] button::after, &[data-label='Due'] button::after, &[data-label='Owner'] button::after {
      content: '';
      position: absolute;
      inset: -4px 0;
    }
    & > * { min-width: 0; }
  }
`

const Check = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  cursor: pointer;
  input { width: 18px; height: 18px; cursor: pointer; accent-color: ${INK}; }
`

const TitleButton = styled.button`
  display: block;
  max-width: 100%;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  color: ${INK};
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  &:hover { text-decoration: underline; }
  &:focus-visible { outline: 2px solid ${INK}; outline-offset: 2px; }
  @media (max-width: 768px) {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: normal;
    overflow-wrap: anywhere;
  }
`

const Sub = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  margin-top: 2px;
  font-size: 13px;
  color: ${MUTED};
  white-space: nowrap;
`

const SubPart = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  &:first-child:not(:last-child) { flex: 0 0 auto; max-width: 50%; }
`

const SourceLink = styled(Link)`
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 13px;
  color: ${LINK};
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  white-space: nowrap;
`

const CommentRow = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin-top: 2px;
  font-size: 13px;
  color: ${MUTED};
  white-space: nowrap;
  @media (max-width: 768px) {
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 0 8px;
    white-space: normal;
  }
`

const CommentText = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  svg { flex: 0 0 auto; font-size: 15px; }
  @media (max-width: 768px) {
    flex: 1 1 100%;
    flex-wrap: wrap;
    align-items: flex-start;
    svg { margin-top: 2px; }
  }
`

const CommentExcerpt = styled.span`
  flex: 0 100 auto;
  min-width: 2em;
  overflow: hidden;
  text-overflow: ellipsis;
  font-style: italic;
  @media (max-width: 768px) {
    flex: 1 1 calc(100% - 20px);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: normal;
  }
`

const CommentBy = styled.span`
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  @media (max-width: 768px) {
    flex: 1 1 100%;
    padding-left: 20px;
  }
`

const CommentCount = styled.button`
  flex: 0 0 auto;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: ${LINK};
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
  white-space: nowrap;
  &:hover { color: ${INK}; }
  &:focus-visible { outline: 2px solid ${INK}; outline-offset: 2px; }
  @media (max-width: 768px) {
    min-height: 40px;
    padding: 0 8px;
    margin: -4px -8px -4px 0;
  }
`

const Created = styled.span`
  font-size: 14px;
  color: ${MUTED};
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`

const ErrorRow = styled.div`
  margin-top: 12px;
`
